import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { showLoader, hideLoader } from "./LoaderSlice";
import {
  productAvailabilities,
  productDevelopment,
  downloadProductAvailabilitiesCSV,
  syncInteractiveEntry,
  getInteractiveMapLogs,
  getAllProducts,
  getPartnerProducts,
  getProductPartners,
  patchExclusionData,
  updateDisplayOnWebsite,
  updateERPFlag,
  uploadProductDevelopmentCSV,
  uploadProductAvailabilitiesCSV,
  fetchDetailedCountryList,
  getExclusionData,
  getAllActiveProductsData,
  getInteractiveMapQuarters
} from "../../services";
import { showMessage } from "../../helpers";

// Create async thunks for API calls
export const interactiveMapLogs = createAsyncThunk(
  "interactiveMaps/getInteractiveMapLogs",
  async (data, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getInteractiveMapLogs(data);
      dispatch(hideLoader());
      return response || [];
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const productAvailabilitiesLogs = createAsyncThunk(
  "interactiveMaps/productAvailabilities",
  async (data, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await productAvailabilities(data);
      dispatch(hideLoader());
      if (response) {
        showMessage("success", "Product availabilities successfully updated");
      }
      return response;
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const productDevelopmentLogs = createAsyncThunk(
  "interactiveMaps/productDevelopment",
  async (data, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await productDevelopment(data);
      dispatch(hideLoader());
      if (response) {
        showMessage("success", "Product development successfully updated");
      }
      return response;
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const downloadProductAvailabilitiesCSVAction = createAsyncThunk(
  "interactiveMaps/downloadProductAvailabilitiesCSV",
  async (data, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await downloadProductAvailabilitiesCSV(data);
      dispatch(hideLoader());
      if (response) {
        showMessage("success", "Product availabilities CSV downloaded successfully");
      }
      return response;
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      showMessage("error", "Failed to download product availabilities CSV");
      throw error;
    }
  },
);

export const syncInteractiveEntryLogs = createAsyncThunk(
  "interactiveMaps/syncInteractiveEntry",
  async (data, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await syncInteractiveEntry(data);
      dispatch(hideLoader());
      if (response) {
        showMessage("success", "Interactive entry synced successfully");
      }
      return response;
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const fetchAllProducts = createAsyncThunk(
  "interactiveMaps/fetchAllProducts",
  async (_, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getAllProducts();
      dispatch(hideLoader());
      return response || [];
    } catch (error) {
      dispatch(hideLoader());
      console.error(error);
      throw error;
    }
  }
);

export const fetchPartnerProducts = createAsyncThunk(
  "interactiveMaps/fetchPartnerProducts",
  async (partnerId, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getPartnerProducts(partnerId);
      dispatch(hideLoader());
      return response || [];
    } catch (error) {
      dispatch(hideLoader());
      console.error(error);
      throw error;
    }
  }
);

export const fetchProductPartners = createAsyncThunk(
  "interactiveMaps/fetchProductPartners",
  async (productId, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getProductPartners(productId);
      dispatch(hideLoader());
      return response || [];
    } catch (error) {
      dispatch(hideLoader());
      console.error(error);
      throw error;
    }
  }
);

export const fetchExclusionData = createAsyncThunk(
  "interactiveMaps/fetchExclusionData",
  async (_, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getExclusionData();
      dispatch(hideLoader());
      return response;
    } catch (error) {
      dispatch(hideLoader());
      console.error(error);
      throw error;
    }
  }
);

export const submitExclusionData = createAsyncThunk(
  "interactiveMaps/submitExclusionData",
  async (payload, { dispatch, getState }) => {
    dispatch(showLoader());
    try {
      // Get the current exclusion data ID from state
      const { id } = getState().interactiveMaps.ExclusionConfig || {};
      if (!id) {
        throw new Error("No exclusion data ID found");
      }
      
      const response = await patchExclusionData(payload, id);
      dispatch(hideLoader());
      showMessage("success", "Exclusion data saved successfully");
      return response;
    } catch (error) {
      dispatch(hideLoader());
      console.error(error);
      throw error;
    }
  }
);

export const updateDisplayFlag = createAsyncThunk(
  "interactiveMaps/updateDisplayFlag",
  async ({ activeProductId, flag }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const res = await updateDisplayOnWebsite(activeProductId, flag);
      dispatch(hideLoader());
      showMessage("success", "Display on Website flag updated");
      return res;
    } catch (err) {
      dispatch(hideLoader());
      throw err;
    }
  }
);

export const updateERPApproval = createAsyncThunk(
  "interactiveMaps/updateERPApproval",
  async ({ productId, flag }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const res = await updateERPFlag(productId, flag);
      dispatch(hideLoader());
      showMessage("success", "ERP Approval updated");
      return res;
    } catch (err) {
      dispatch(hideLoader());
      throw err;
    }
  }
);

export const fetchInteractiveMapQuarters = createAsyncThunk(
  "interactiveMaps/fetchInteractiveMapQuarters",
  async (_, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getInteractiveMapQuarters();
      dispatch(hideLoader());
      return response || [];
    } catch (error) {
      dispatch(hideLoader());
      console.error(error);
      throw error;
    }
  }
);

export const uploadDevelopmentCSV = createAsyncThunk(
  "interactiveMaps/uploadDevelopmentCSV",
  async (formData, { dispatch }) => {
    dispatch(showLoader());
    try {
      const res = await uploadProductDevelopmentCSV(formData);
      dispatch(hideLoader());
      showMessage("success", "Development CSV uploaded");
      return res;
    } catch (err) {
      dispatch(hideLoader());
      throw err;
    }
  }
);

export const uploadAvailabilityCSV = createAsyncThunk(
  "interactiveMaps/uploadAvailabilityCSV",
  async (formData, { dispatch }) => {
    dispatch(showLoader());
    try {
      const res = await uploadProductAvailabilitiesCSV(formData);
      dispatch(hideLoader());
      showMessage("success", "Availability CSV uploaded");
      return res;
    } catch (err) {
      dispatch(hideLoader());
      throw err;
    }
  }
);

export const getCountryList = createAsyncThunk(
  "interactiveMaps/getCountryList",
  async (_, { dispatch }) => {
    dispatch(showLoader());
    try {
      const res = await fetchDetailedCountryList();
      dispatch(hideLoader());
      return res;
    } catch (err) {
      dispatch(hideLoader());
      throw err;
    }
  }
);

export const fetchAllActiveProductsData = createAsyncThunk(
  "interactiveMaps/fetchAllActiveProductsData",
  async (_, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getAllActiveProductsData();
      dispatch(hideLoader());
      return response || [];
    } catch (error) {
      dispatch(hideLoader());
      console.error(error);
      throw error;
    }
  }
);

const initialState = {
  InteractiveMapList: [],
  Quarters: [],
  AllProducts: [],
  PartnerProducts: [],
  ActiveProducts: [],
  ProductPartners: [],
  ExclusionConfig: null,
  CountryList: [],
  UploadStatus: null,
  DisplayFlagStatus: null,
  ERPFlagStatus: null,
  isLoaded: false,
};

const interactiveMapsSlice = createSlice({
  name: "interactiveMaps",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get Interactive Map Logs
      .addCase(interactiveMapLogs.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(interactiveMapLogs.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.InteractiveMapList = action.payload || [];
      })
      .addCase(interactiveMapLogs.rejected, (state) => {
        state.isLoaded = true;
      })

      // Product Availabilities
      .addCase(productAvailabilitiesLogs.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(productAvailabilitiesLogs.fulfilled, (state) => {
        state.isLoaded = true;
      })
      .addCase(productAvailabilitiesLogs.rejected, (state) => {
        state.isLoaded = true;
      })

      // Product Development
      .addCase(productDevelopmentLogs.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(productDevelopmentLogs.fulfilled, (state) => {
        state.isLoaded = true;
      })
      .addCase(productDevelopmentLogs.rejected, (state) => {
        state.isLoaded = true;
      })

      // Download Product Availabilities CSV
      .addCase(downloadProductAvailabilitiesCSVAction.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(downloadProductAvailabilitiesCSVAction.fulfilled, (state) => {
        state.isLoaded = true;
      })
      .addCase(downloadProductAvailabilitiesCSVAction.rejected, (state) => {
        state.isLoaded = true;
      })

      // Sync Interactive Entry
      .addCase(syncInteractiveEntryLogs.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(syncInteractiveEntryLogs.fulfilled, (state) => {
        state.isLoaded = true;
      })
      .addCase(syncInteractiveEntryLogs.rejected, (state) => {
        state.isLoaded = true;
      })

      // Fetch All Products
      .addCase(fetchAllProducts.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.AllProducts = action.payload;
      })
      .addCase(fetchAllProducts.rejected, (state) => {
        state.isLoaded = true;
      })

      // Fetch Partner Products
      .addCase(fetchPartnerProducts.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(fetchPartnerProducts.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.PartnerProducts = action.payload;
      })
      .addCase(fetchPartnerProducts.rejected, (state) => {
        state.isLoaded = true;
      })

      // Fetch Product Partners
      .addCase(fetchProductPartners.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(fetchProductPartners.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.ProductPartners = action.payload;
      })
      .addCase(fetchProductPartners.rejected, (state) => {
        state.isLoaded = true;
      })

      // Fetch Exclusion Data
      .addCase(fetchExclusionData.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(fetchExclusionData.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.ExclusionConfig = action.payload;
      })
      .addCase(fetchExclusionData.rejected, (state) => {
        state.isLoaded = true;
      })

      // Submit Exclusion Data
      .addCase(submitExclusionData.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(submitExclusionData.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.ExclusionConfig = action.payload;
      })
      .addCase(submitExclusionData.rejected, (state) => {
        state.isLoaded = true;
      })

      .addCase(fetchAllActiveProductsData.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(fetchAllActiveProductsData.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.ActiveProducts = action.payload;
      })
      .addCase(fetchAllActiveProductsData.rejected, (state) => {
        state.isLoaded = true;
      })

      .addCase(getCountryList.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(getCountryList.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.CountryList = action.payload || [];
      })
      .addCase(getCountryList.rejected, (state) => {
        state.isLoaded = true;
      })

      // Fetch Interactive Map Quarters
      .addCase(fetchInteractiveMapQuarters.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(fetchInteractiveMapQuarters.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.Quarters = action.payload || [];
      })
      .addCase(fetchInteractiveMapQuarters.rejected, (state) => {
        state.isLoaded = true;
      })

      .addCase(uploadDevelopmentCSV.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(uploadDevelopmentCSV.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.UploadStatus = "success";
      })
      .addCase(uploadDevelopmentCSV.rejected, (state) => {
        state.isLoaded = true;
        state.UploadStatus = "error";
      })

      .addCase(uploadAvailabilityCSV.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(uploadAvailabilityCSV.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.UploadStatus = "success";
      })
      .addCase(uploadAvailabilityCSV.rejected, (state) => {
        state.isLoaded = true;
        state.UploadStatus = "error";
      })

      .addCase(updateDisplayFlag.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(updateDisplayFlag.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.DisplayFlagStatus = "success";
      })
      .addCase(updateDisplayFlag.rejected, (state) => {
        state.isLoaded = true;
        state.DisplayFlagStatus = "error";
      })

      .addCase(updateERPApproval.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(updateERPApproval.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.ERPFlagStatus = "success";
      })
      .addCase(updateERPApproval.rejected, (state) => {
        state.isLoaded = true;
        state.ERPFlagStatus = "error";
      })
  },
});

export default interactiveMapsSlice.reducer;